import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useHits, useInstantSearch, useSearchBox } from 'react-instantsearch';

import ContactFormDialog from '@/components/ContactFormDialog/ContactFormDialog';
import SearchEngine from '@/components/SearchEngine/SearchEngine';
import { SESSION_STORAGE_KEYS } from '@/constants/sessionStorageKeys';
import { RENTAL_PRODUCTS_PATH } from '@/helpers/constants';
import {
  trackHeroContactFormOpen,
  trackHeroSearchClear,
  trackHeroSearchError,
  trackHeroSearchFocus,
  trackHeroSearchQuery,
  trackHeroSearchSelectFreetext,
  trackHeroSearchSelectProduct,
  trackHeroSearchZeroResults,
} from '@/helpers/heroSearchEvents';
import { removeSessionStorageItem } from '@/helpers/sessionStorage';
import { useBasketContext } from '@/hooks/useBasketContext';
import useFormatMessage from '@/hooks/useFormatMessage';
import { useMobileMediaQuery } from '@/hooks/useMobileMediaQuery';

import AutocompleteDropdown from './AutocompleteDropdown';
import { buildFreeTextRequest } from './buildFreeTextRequest';
import CategorySearch from './CategorySearch';
import FreeTextRow from './FreeTextRow';
import HeroSearchErrorBoundary from './HeroSearchErrorBoundary';
import MobileSearchSheet from './MobileSearchSheet';
import { loadPersistedState, persistState } from './persistence';
import { Root } from './RentalRequestFlow.style';
import SearchInput from './SearchInput';
import { initialState, reducer, STEPS } from './state';

function FlowInner({ defaultQuery, defaultProductSlug, surface }) {
  const formatMessage = useFormatMessage();
  const isMobile = useMobileMediaQuery();
  const [state, dispatch] = useReducer(reducer, initialState);
  const hydratedRef = useRef(false);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listboxId = useId();
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const focusTrackedRef = useRef(false);
  const queryStartRef = useRef({ query: '', t: 0 });
  const lastEmptyStateRef = useRef(null);
  const reportedQueryRef = useRef(null);
  const staleResultsRef = useRef(null);
  const [staleResultsForMemo, setStaleResultsForMemo] = useState(null);

  useEffect(() => {
    if (hydratedRef.current) return;
    const persisted = loadPersistedState();
    const seeded =
      defaultQuery && !persisted.query
        ? { ...persisted, query: defaultQuery }
        : persisted;
    dispatch({ type: 'HYDRATE', snapshot: seeded });
    hydratedRef.current = true;
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return;
    persistState(state);
  }, [state]);

  const queryHook = useMemo(() => debounce((q, search) => search(q), 200), []);
  const { refine } = useSearchBox({ queryHook });
  const { items: rawHits, results } = useHits();
  const { status: algoliaStatus } = useInstantSearch();

  const router = useRouter();
  const { addRequestToBasket } = useBasketContext();

  useEffect(() => {
    if (!hydratedRef.current) return;
    refine(state.query);
  }, [state.query, refine]);

  const [categoryHits, setCategoryHits] = useState([]);
  const productHits = rawHits;
  const categoryRows = categoryHits;
  const resultRowCount = productHits.length + categoryRows.length;
  const isLoading = results == null;
  const dropdownOpen = state.step === STEPS.S1;

  const emptyState = useMemo(() => {
    if (!dropdownOpen) return null;
    if (algoliaStatus === 'error') return 'error';
    if (
      !isLoading &&
      results &&
      results !== staleResultsForMemo &&
      resultRowCount === 0 &&
      state.query.trim()
    ) {
      return 'zero_results';
    }
    return null;
  }, [
    dropdownOpen,
    algoliaStatus,
    isLoading,
    results,
    staleResultsForMemo,
    resultRowCount,
    state.query,
  ]);

  const freeTextRowCount =
    dropdownOpen && !emptyState && !isMobile && state.query.trim() ? 1 : 0;
  const totalRows = resultRowCount + freeTextRowCount;

  const [contactOpen, setContactOpen] = useState(false);

  const handleOpenContact = useCallback(() => {
    trackHeroContactFormOpen(surface, {
      source: emptyState === 'error' ? 'algolia_error' : 'zero_results',
    });
    setContactOpen(true);
  }, [surface, emptyState]);

  const freeTextInFlightRef = useRef(false);

  const handleRequestFreeText = useCallback(
    async rawQuery => {
      const trimmed = (rawQuery ?? state.query).trim();
      if (!trimmed) return;
      if (freeTextInFlightRef.current) return;
      freeTextInFlightRef.current = true;
      trackHeroSearchSelectFreetext(surface, {
        query: trimmed,
        source: emptyState ? 'zero_results' : 'pinned_row',
      });
      try {
        await addRequestToBasket(buildFreeTextRequest(trimmed));
        focusTrackedRef.current = false;
        removeSessionStorageItem(SESSION_STORAGE_KEYS.RENTAL_REQUEST_FLOW);
        if (router.pathname !== '/basket') {
          await router.push('/basket');
        }
        // Not reset on success: the flow navigates away.
      } catch (err) {
        freeTextInFlightRef.current = false;
        throw err;
      }
    },
    [surface, state.query, emptyState, addRequestToBasket, router]
  );

  useEffect(() => {
    const hasQuery = !!state.query.trim();
    setHighlightedIndex(hasQuery && totalRows > 0 ? 0 : -1);
  }, [state.query, totalRows]);

  const trackProductSelection = useCallback(
    hit => {
      const position = productHits.indexOf(hit);
      trackHeroSearchSelectProduct(surface, {
        query: state.query,
        productSlug: hit?.slug ?? null,
        position: position >= 0 ? position : null,
        section: 'products',
      });
    },
    [surface, state.query, productHits]
  );

  const handleSelectProduct = useCallback(
    hit => {
      if (!hit?.slug) return;
      trackProductSelection(hit);
      router.push(`${RENTAL_PRODUCTS_PATH}/${hit.slug}`);
    },
    [trackProductSelection, router]
  );

  const handleSelectCategory = useCallback(
    row => {
      if (row?.url) {
        router.push(row.url);
      }
    },
    [router]
  );

  const handleFocus = useCallback(() => {
    dispatch({ type: 'FOCUS_INPUT' });
    if (!focusTrackedRef.current) {
      trackHeroSearchFocus(surface);
      focusTrackedRef.current = true;
    }
  }, [surface]);

  const handleClosePanel = useCallback(() => {
    dispatch({ type: 'CLOSE_PANEL' });
  }, []);

  useEffect(() => {
    if (!dropdownOpen || isMobile) return undefined;
    const handlePointerDown = e => {
      if (rootRef.current?.contains(e.target)) return;
      if (document.getElementById(listboxId)?.contains(e.target)) return;
      dispatch({ type: 'CLOSE_PANEL' });
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [dropdownOpen, isMobile, listboxId]);

  const handleClear = useCallback(() => {
    trackHeroSearchClear(surface);
    dispatch({ type: 'CLEAR' });
    focusTrackedRef.current = false;
    inputRef.current?.focus();
  }, [surface]);

  const handleKeyDown = useCallback(
    e => {
      if (!dropdownOpen) {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (totalRows === 0) return;
        setHighlightedIndex(i => (i + 1) % Math.max(totalRows, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (totalRows === 0) return;
        setHighlightedIndex(i => (i - 1 + totalRows) % Math.max(totalRows, 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex < 0) return;
        if (highlightedIndex < productHits.length) {
          handleSelectProduct(productHits[highlightedIndex]);
        } else if (
          highlightedIndex <
          productHits.length + categoryRows.length
        ) {
          handleSelectCategory(
            categoryRows[highlightedIndex - productHits.length]
          );
        } else {
          handleRequestFreeText(state.query);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        dispatch({ type: 'CLOSE_PANEL' });
      }
    },
    [
      dropdownOpen,
      totalRows,
      highlightedIndex,
      productHits,
      categoryRows,
      handleSelectProduct,
      handleSelectCategory,
      handleRequestFreeText,
      state.query,
    ]
  );

  const activeOptionId = (() => {
    if (highlightedIndex < 0) return null;
    if (highlightedIndex < productHits.length) {
      return `${listboxId}-product-${highlightedIndex}`;
    }
    if (highlightedIndex < productHits.length + categoryRows.length) {
      return `${listboxId}-category-${highlightedIndex - productHits.length}`;
    }
    return `${listboxId}-freetext`;
  })();

  const [announcement, setAnnouncement] = useState('');

  const formatMessageRef = useRef(formatMessage);
  useEffect(() => {
    formatMessageRef.current = formatMessage;
  }, [formatMessage]);

  const announceCounts = useMemo(
    () =>
      debounce((products, categories) => {
        const fm = formatMessageRef.current;
        if (products + categories === 0) {
          setAnnouncement(fm('rental_request_flow_a11y_no_results'));
        } else {
          setAnnouncement(
            fm('rental_request_flow_a11y_results', { products, categories })
          );
        }
      }, 500),
    []
  );

  useEffect(() => () => announceCounts.cancel(), [announceCounts]);

  useEffect(() => {
    if (!dropdownOpen) return;
    announceCounts(productHits.length, categoryRows.length);
  }, [dropdownOpen, productHits.length, categoryRows.length, announceCounts]);

  useEffect(() => {
    if (state.query.trim()) {
      queryStartRef.current = { query: state.query, t: performance.now() };
      staleResultsRef.current = results;
      setStaleResultsForMemo(results);
      reportedQueryRef.current = null;
    }
  }, [state.query]);

  const latestCountsRef = useRef({ products: 0, categories: 0 });
  useEffect(() => {
    latestCountsRef.current = {
      products: productHits.length,
      categories: categoryRows.length,
    };
  }, [productHits.length, categoryRows.length]);

  const trackQueryDebounced = useMemo(
    () =>
      debounce((surfaceArg, query, latencyMs) => {
        trackHeroSearchQuery(surfaceArg, {
          query,
          resultsCountProducts: latestCountsRef.current.products,
          resultsCountCategories: latestCountsRef.current.categories,
          latencyMs,
        });
      }, 300),
    []
  );
  useEffect(() => () => trackQueryDebounced.cancel(), [trackQueryDebounced]);

  useEffect(() => {
    if (!state.query.trim() || !results) return;
    if (queryStartRef.current.query !== state.query) return;
    if (results === staleResultsRef.current) return;
    if (reportedQueryRef.current === state.query) return;
    reportedQueryRef.current = state.query;
    trackQueryDebounced(
      surface,
      state.query,
      Math.round(performance.now() - queryStartRef.current.t)
    );
  }, [state.query, results, surface, trackQueryDebounced]);

  useEffect(() => {
    if (!dropdownOpen) {
      lastEmptyStateRef.current = null;
      return;
    }
    if (lastEmptyStateRef.current === emptyState) return;
    lastEmptyStateRef.current = emptyState;
    if (emptyState === 'zero_results') {
      trackHeroSearchZeroResults(surface, { query: state.query });
    } else if (emptyState === 'error') {
      trackHeroSearchError(surface, {
        errorType: 'algolia',
        query: state.query,
      });
    }
  }, [dropdownOpen, emptyState, surface, state.query]);

  void defaultProductSlug;

  const sheetOpen = isMobile && state.step === STEPS.S1;

  const functionalSearchInput = (
    <SearchInput
      ref={inputRef}
      value={state.query}
      onChange={query => dispatch({ type: 'QUERY_CHANGED', query })}
      onFocus={handleFocus}
      onClear={handleClear}
      onKeyDown={handleKeyDown}
      listboxId={listboxId}
      activeOptionId={activeOptionId}
      expanded={dropdownOpen}
    />
  );

  const facadeSearchInput = (
    <SearchInput
      value={state.query}
      onChange={() => {}}
      onFocus={() => {}}
      onClear={handleClear}
      onKeyDown={() => {}}
      listboxId={listboxId}
      expanded={false}
      triggerMode
      onTrigger={handleFocus}
    />
  );

  const dropdown = (
    <AutocompleteDropdown
      open={dropdownOpen}
      anchorEl={rootRef.current}
      listboxId={listboxId}
      loading={isLoading}
      productHits={productHits}
      categoryRows={categoryRows}
      highlightedIndex={highlightedIndex}
      productHeading={formatMessage('rental_request_flow_section_products')}
      categoryHeading={formatMessage('rental_request_flow_section_categories')}
      emptyProducts={formatMessage('rental_request_flow_no_products')}
      emptyState={emptyState}
      query={state.query}
      variant={isMobile ? 'inline' : 'popper'}
      showFreeTextRow={!isMobile}
      onSelectProduct={trackProductSelection}
      onSelectCategory={handleSelectCategory}
      onRequestFreeText={handleRequestFreeText}
      onOpenContact={handleOpenContact}
      onHover={setHighlightedIndex}
    />
  );

  return (
    <Root
      data-test-subject="rental-request-flow"
      data-surface={surface}
      data-step={state.step}
      ref={rootRef}>
      <CategorySearch onResults={setCategoryHits} />
      {isMobile ? (
        <>
          {facadeSearchInput}
          <MobileSearchSheet
            open={sheetOpen}
            onClose={handleClosePanel}
            onEntered={() => inputRef.current?.focus()}
            searchInput={functionalSearchInput}
            dropdown={dropdownOpen ? dropdown : undefined}
            footer={
              !emptyState && (
                <FreeTextRow
                  query={state.query}
                  onSelect={handleRequestFreeText}
                  asOption={false}
                />
              )
            }
          />
        </>
      ) : (
        <>
          {functionalSearchInput}
          {dropdown}
        </>
      )}
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}>
        {announcement}
      </span>
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </Root>
  );
}

export default function RentalRequestFlow(props) {
  return (
    <HeroSearchErrorBoundary>
      <SearchEngine>
        <FlowInner {...props} />
      </SearchEngine>
    </HeroSearchErrorBoundary>
  );
}

RentalRequestFlow.propTypes = {
  defaultQuery: PropTypes.string,
  defaultProductSlug: PropTypes.string,
  surface: PropTypes.oneOf(['homepage', 'category_page', 'landing_page']),
};

RentalRequestFlow.defaultProps = {
  defaultQuery: '',
  defaultProductSlug: null,
  surface: 'homepage',
};
